import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useViewportHeight } from "./hooks/useViewportHeight";
import LandingPage from "./pages/LandingPage";
import StoresPreview from "./pages/StoresPreview";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Auth from "./pages/Auth";
import Home from "./pages/Home";
import AdminDashboard from "./pages/AdminDashboard";
import ScanQR from "./pages/ScanQR";
import Caja from "./pages/Caja";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SubscriptionRequired from "./components/SubscriptionRequired";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import AdminRoute from "./components/AdminRoute";
import CloserRoute from "./components/CloserRoute";
import CloserPanel from "./pages/CloserPanel";
import BrandLab from "./pages/BrandLab";
import Prepago from "./pages/Prepago";
import Bienvenida from "./pages/Bienvenida";
import ElegirBeneficio from "./pages/ElegirBeneficio";
import EnlaceEntrada from "./pages/EnlaceEntrada";
import { useCapturaEnlace } from "./hooks/useCapturaEnlace";

/* Un componente sin pintura: solo existe para que el hook pueda leer la URL,
   que es algo que únicamente se puede hacer dentro del router. */
function CapturaEnlace() {
  useCapturaEnlace();
  return null;
}

function App() {
  useViewportHeight();
  return (
    <div className="App">
      <BrowserRouter>
        {/*
          Recoge el enlace de referido (`?ref=`, `?code=` o `?utm_source=`) en
          cualquier ruta. Va dentro del router porque necesita leer la URL, y
          antes de las rutas porque tiene que correr aunque la persona aterrice
          en la portada.
        */}
        <CapturaEnlace />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {/* El enlace corto de closers y canales: vondi.mx/r/CODIGO. */}
          <Route path="/r/:codigo" element={<EnlaceEntrada />} />
          <Route path="/stores-preview" element={<StoresPreview />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/*
            Caja de tienda. La tienda va en la URL, no en un desplegable: es lo
            que impide que un canje quede atribuido a otra tienda. `/scan`
            sobrevive solo como aviso para las cajas que lo tengan guardado.
          */}
          <Route path="/t/:slug" element={<Caja />} />
          <Route path="/scan" element={<ScanQR />} />
          {/*
            Pasos 2 y 3 del flujo: el visitante toca «Paga menos» y entra al
            perfil SIN crear cuenta. Ve los beneficios con candado, elige el
            gratis, y solo entonces se le piden sus datos. Por eso son rutas
            públicas y no van envueltas en ProtectedRoute.
          */}
          <Route path="/beneficios" element={<Bienvenida />} />
          <Route path="/beneficios/elegir" element={<ElegirBeneficio />} />
          {/* Banco de pruebas del sistema visual (BRAND.md + FLIPCARD_SPEC §9).
              Herramienta interna de verificación, sin datos ni acciones. */}
          <Route path="/brand-lab" element={<BrandLab />} />
          
          {/* Rutas públicas (redirigen a /home si ya está logueado) */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Auth mode="login" />
              </PublicRoute>
            } 
          />
          <Route 
            path="/signup" 
            element={
              <PublicRoute>
                <Auth mode="signup" />
              </PublicRoute>
            } 
          />
          <Route 
            path="/forgot-password" 
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reset-password" 
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } 
          />
          
          {/*
            El checkout, abierto también sin sesión (16-sep-2026): quien llega
            por la ruta normal crea su cuenta y paga en esta misma pantalla. Con
            sesión funciona como antes.
          */}
          <Route path="/subscription" element={<SubscriptionRequired />} />
          
          {/*
            Zona de miembros — ahora abierta a cuentas GRATUITAS (Módulo 1, freemium).

            Antes iba envuelta en <RequireSubscription>, que rebotaba a /subscription
            a cualquiera sin membresía activa: era el único muro entre un usuario
            recién registrado y la app. Ese muro se movió hacia adentro — las
            tarjetas premium se ven en tono tenue y con candado, y el paywall
            aparece al intentar desbloquear la segunda.

            ⚠️ Al desplegar esto, las ~2,600 cuentas que ya existen sin haber pagado
            nunca ganan acceso al tier gratuito de golpe. Es el objetivo del
            freemium, pero tiene que ser una decisión consciente, no una sorpresa.
          */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          
          {/* Prepago: el momento de venta, justo después de que el usuario
              comprueba con dinero real cuánto le ahorró su beneficio. */}
          <Route
            path="/prepago"
            element={
              <ProtectedRoute>
                <Prepago />
              </ProtectedRoute>
            }
          />

          {/* Ruta admin (requiere autenticación + rol admin) */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          {/* Panel de closers (requiere autenticación + rol closer) */}
          <Route
            path="/closer"
            element={
              <CloserRoute>
                <CloserPanel />
              </CloserRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
